import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { AlertCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { showToast } from '../utils/errorHandler';
import GoogleLoginButton from '../components/Auth/GoogleLoginButton';
import AppleLoginButton from '../components/Auth/AppleLoginButton';
import { RECRUITER_DEMO_URL } from '../constants/recruiterContact';
import { consumePostAuthRedirect } from '../lib/postAuthRedirect';
import { useViewMode } from '../contexts/ViewModeContext';

const Login: React.FC = () => {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { signIn } = useAuth();
  const { setViewMode } = useViewMode();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isRecruiterMode = searchParams.get('mode') === 'recruiter';

  // Check for account deleted error from sessionStorage
  useEffect(() => {
    const accountDeleted = sessionStorage.getItem('accountDeletedError');
    if (accountDeleted === 'true') {
      // Show toast notification
      showToast(t('errors.accountDeleted'), 'error');
      // Clear the flag so it doesn't show again
      sessionStorage.removeItem('accountDeletedError');
    }
  }, [t]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const { error } = await signIn(email, password);
      if (error) {
        // Check for unconfirmed email
        const errorMsg = error.message?.toLowerCase() || '';
        if (errorMsg.includes('email not confirmed') || errorMsg.includes('not confirmed')) {
          setError(t('login.errorEmailNotConfirmed'));
        } else {
          setError(error.message || t('login.errorSignInFailed'));
        }
      } else {
        // Successful login - get session to check user type (no extra API call!)
        const { data: { session } } = await supabase.auth.getSession();
        const isRecruiter = session?.user?.user_metadata?.is_recruiter || false;

        // Reset the shell view-mode to the account's natural experience on login
        // (navigation may still follow ?return=, but the shown audience resets).
        setViewMode(isRecruiter ? 'recruiter' : 'explorer');

        // Redirect priority:
        //   1. ?return= query param (fast path -- set when user lands
        //      directly on /login from a CTA)
        //   2. localStorage from rememberPostAuthRedirect() (survives
        //      the email-confirmation round-trip on signup → confirm
        //      → login chains, e.g. coming back from an invite link)
        //   3. Default per role (recruiter / explorer)
        const returnUrl = searchParams.get('return') || consumePostAuthRedirect();
        if (returnUrl && returnUrl.startsWith('/')) {
          navigate(returnUrl);
        } else {
          navigate(isRecruiter ? '/?tab=hire' : '/');
        }
      }
    } catch (err: any) {
      setError(err.message || t('login.errorUnexpected'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <h2 className="text-center text-2xl sm:text-3xl font-bold text-slate-900">
          {t('login.title').replace('Cevee', '')}
          <span className="bg-gradient-to-r from-pink-500 to-rose-500 bg-clip-text text-transparent">Cevee</span>
        </h2>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow-sm border border-slate-200 sm:rounded-2xl sm:px-10">
          {error && (
            <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-red-800">
                <p>{error}</p>
                {error.toLowerCase().includes('not confirmed') && (
                  <Link 
                    to="/resend-confirmation" 
                    className="mt-2 inline-block font-medium text-pink-600 hover:text-pink-700 underline"
                  >
                    {t('login.resendConfirmation')}
                  </Link>
                )}
              </div>
            </div>
          )}

          {/* Social Login (hidden for recruiter mode) */}
          {!isRecruiterMode && (
            <>
              <p className="text-[10px] text-gray-400 text-center mb-2">
                {t('login.socialExplorerHint', 'Social login only for Explorer accounts')}
              </p>
              <div className="space-y-3">
                <GoogleLoginButton disabled={loading} />
                <AppleLoginButton disabled={loading} />
              </div>
              
              <p className="text-[10px] text-gray-500 mt-3 text-center">
                {t('signup.socialTermsText', 'By continuing, you agree to our')}{' '}
                <Link to="/terms" className="underline hover:text-gray-700">{t('signup.termsLink')}</Link>
                {' '}{t('signup.termsAnd')}{' '}
                <Link to="/privacy" className="underline hover:text-gray-700">{t('signup.privacyLink')}</Link>
                {t('signup.socialTermsSuffix', '')}
              </p>

              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300" />
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-white text-gray-500">{t('login.orContinueWith', 'or')}</span>
                </div>
              </div>
            </>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                {t('login.emailLabel')}
              </label>
              <input
                id="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                placeholder={t('login.emailPlaceholder')}
                disabled={loading}
              />
            </div>

            <div>
              <div className="flex items-center justify-between">
                <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                  {t('login.passwordLabel')}
                </label>
                <Link
                  to="/forgot-password"
                  className="text-sm font-medium text-pink-600 hover:text-pink-500 transition-colors"
                >
                  {t('login.forgotPassword')}
                </Link>
              </div>
              <input
                id="password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                placeholder={t('login.passwordPlaceholder')}
                disabled={loading}
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full flex justify-center py-2.5 px-4 rounded-xl shadow-sm text-sm font-semibold text-white bg-gradient-to-r from-pink-500 to-rose-500 hover:from-pink-600 hover:to-rose-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-pink-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
            >
              {loading ? t('login.signingInButton') : t('login.signInButton')}
            </button>
          </form>

          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-gray-500">{t('login.newToCevee')}</span>
              </div>
            </div>

            <div className="mt-6">
              <Link
                to="/signup"
                className="w-full flex justify-center py-2.5 px-4 border border-gray-300 rounded-lg shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-pink-500 transition-colors"
              >
                {t('login.createAccount')}
              </Link>
            </div>
          </div>

          <div className="mt-6 pt-4 border-t border-gray-100 text-center">
            <p className="text-xs text-gray-500">
              {t('signup.recruiterInvitedIntro')}{' '}
              <a
                href={RECRUITER_DEMO_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="font-medium text-pink-600 hover:text-pink-700 transition-colors"
              >
                {t('signup.recruiterInvitedCta')} →
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
